import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { Skeleton } from "@/components/ui/skeleton";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProductsProvider } from "@/contexts/ProductsContext";
import { DiscountBarProvider } from "@/components/StickyDiscountBar";
import { captureUTMParams } from "@/utils/utmCapture";
import { PageViewTracker } from "@/hooks/usePageViewTracking";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";
const Gate = lazy(() => import("./pages/Gate"));

// Lazy-loaded routes to reduce initial JS bundle
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const SeoGuide = lazy(() => import("./pages/SeoGuide"));
const IngredientsPage = lazy(() => import("./pages/Ingredients"));
const IngredientDetailPage = lazy(() => import("./pages/IngredientDetail"));
const PrivacyPage = lazy(() => import("./pages/legal/Privacy"));
const TermsPage = lazy(() => import("./pages/legal/Terms"));
const RefundPage = lazy(() => import("./pages/legal/Refund"));
const ShippingPage = lazy(() => import("./pages/legal/Shipping"));
const LeadCapturePopup = lazy(() => import("@/components/LeadCapturePopup"));
const About = lazy(() => import("./pages/About"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const Wholesale = lazy(() => import("./pages/Wholesale"));
const Contact = lazy(() => import("./pages/Contact"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPostPage = lazy(() => import("./pages/BlogPost"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Account = lazy(() => import("./pages/Account"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const DashboardPage = lazy(() => import("./pages/admin/DashboardPage"));
const GrowthPage = lazy(() => import("./pages/admin/GrowthPage"));
const ProductsPage = lazy(() => import("./pages/admin/ProductsPage"));
const OrdersPage = lazy(() => import("./pages/admin/OrdersPage"));
const CheckoutStatusPage = lazy(() => import("./pages/admin/CheckoutStatusPage"));
const SalesPipelinePage = lazy(() => import("./pages/admin/SalesPipelinePage"));
const CustomersPage = lazy(() => import("./pages/admin/CustomersPage"));
const CustomerProfilePage = lazy(() => import("./pages/admin/CustomerProfilePage"));
const FavoritesPage = lazy(() => import("./pages/admin/FavoritesPage"));
const SharesPage = lazy(() => import("./pages/admin/SharesPage"));
const LogsPage = lazy(() => import("./pages/admin/LogsPage"));
const EmailsPage = lazy(() => import("./pages/admin/EmailsPage"));
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage"));
const UserManagementPage = lazy(() => import("./pages/admin/UserManagementPage"));
const StaffManagementPage = lazy(() => import("./pages/admin/StaffManagementPage"));
const TrustedDevicesPage = lazy(() => import("./pages/admin/TrustedDevicesPage"));
const SecurityPage = lazy(() => import("./pages/admin/SecurityPage"));
const JournalPage = lazyWithRetry(() => import("./pages/admin/JournalPage"));
const CommandCenterPage = lazy(() => import("./pages/admin/CommandCenterPage"));
const LabLayout = lazy(() => import("./pages/admin/lab/LabLayout"));
const LabOverview = lazy(() => import("./pages/admin/lab/LabOverview"));
const LabMaterials = lazy(() => import("./pages/admin/lab/LabMaterials"));
const LabMaterialDetail = lazy(() => import("./pages/admin/lab/LabMaterialDetail"));
const LabSuppliers = lazy(() => import("./pages/admin/lab/LabSuppliers"));
const LabFormulas = lazy(() => import("./pages/admin/lab/LabFormulas"));
const LabProduction = lazy(() => import("./pages/admin/lab/LabProduction"));
const LabInventoryLog = lazy(() => import("./pages/admin/lab/LabInventoryLog"));
const OperationsLayout = lazy(() => import("./pages/admin/operations/OperationsLayout"));
const FinanceCampaigns = lazy(() => import("./pages/admin/finance/FinanceCampaigns"));
const FinanceCash = lazy(() => import("./pages/admin/finance/FinanceCash"));
const FinanceOrderPnL = lazy(() => import("./pages/admin/finance/FinanceOrderPnL"));
const FinancePeriodPnL = lazy(() => import("./pages/admin/finance/FinancePeriodPnL"));
const OperationsMaterials = lazy(() => import("./pages/admin/operations/OperationsMaterials"));
const OperationsProduction = lazy(() => import("./pages/admin/operations/OperationsProduction"));
const OperationsPackaging = lazy(() => import("./pages/admin/operations/OperationsPackaging"));
const OperationsLabor = lazy(() => import("./pages/admin/operations/OperationsLabor"));
const OperationsExpenses = lazy(() => import("./pages/admin/operations/OperationsExpenses"));
const OperationsCostEngine = lazy(() => import("./pages/admin/operations/OperationsCostEngine"));
const OperationsOverview = lazy(() => import("./pages/admin/operations/OperationsOverview"));
const ProductionActive = lazy(() => import("./pages/admin/operations/ProductionActive"));
const InventoryOverview = lazy(() => import("./pages/admin/inventory/InventoryOverview"));
const InventoryReservationsPage = lazy(() => import("./pages/admin/inventory/InventoryReservationsPage"));
const FinishedGoodsPage = lazy(() => import("./pages/admin/inventory/FinishedGoodsPage"));
const ProductProfitabilityPage = lazy(() => import("./pages/admin/finance/ProductProfitabilityPage"));
const ExecutiveSnapshotPage = lazy(() => import("./pages/admin/executive/ExecutiveSnapshotPage"));
const ProductIntelligencePage = lazy(() => import("./pages/admin/executive/ProductIntelligencePage"));
const InventoryIntelligencePage = lazy(() => import("./pages/admin/executive/InventoryIntelligencePage"));
const TrendsPage = lazy(() => import("./pages/admin/executive/TrendsPage"));
const LandingPageEditor = lazy(() => import("./pages/admin/LandingPageEditor"));
const CampaignsPage = lazy(() => import("./pages/admin/CampaignsPage"));
const LandingPagesAdPage = lazy(() => import("./pages/admin/LandingPagesAdPage"));
const LeadsPage = lazy(() => import("./pages/admin/LeadsPage"));
const PromoCodesPage = lazy(() => import("./pages/admin/PromoCodesPage"));
const SiteContentPage = lazy(() => import("./pages/admin/SiteContentPage"));
const NavigationManagerPage = lazy(() => import("./pages/admin/NavigationManagerPage"));
const BackgroundsPage = lazy(() => import("./pages/admin/BackgroundsPage"));
const SeoPage = lazy(() => import("./pages/admin/SeoPage"));
const SeoKeywordTrackingPage = lazy(() => import("./pages/admin/SeoKeywordTrackingPage"));
const AdminNotFound = lazy(() => import("./pages/admin/AdminNotFound"));
const AiSettingsPage = lazy(() => import("./pages/admin/AiSettingsPage"));
const ContentStudioPage = lazy(() => import("./pages/admin/ContentStudioPage"));
const ComingSoonPage = lazy(() => import("./pages/admin/ComingSoonPage"));
const AuditsIndexPage = lazy(() => import("./pages/admin/audits/AuditsIndexPage"));
const OrdersFunnelAuditPage = lazy(() => import("./pages/admin/audits/OrdersFunnelAuditPage"));
const BlogPerformanceAuditPage = lazy(() => import("./pages/admin/audits/BlogPerformanceAuditPage"));
const AuthSecurityAuditPage = lazy(() => import("./pages/admin/audits/AuthSecurityAuditPage"));
const EdgeFunctionsAuditPage = lazy(() => import("./pages/admin/audits/EdgeFunctionsAuditPage"));
const DatabaseHealthAuditPage = lazy(() => import("./pages/admin/audits/DatabaseHealthAuditPage"));
const MetricsEndpointsAuditPage = lazy(() => import("./pages/admin/audits/MetricsEndpointsAuditPage"));
const I18nCoverageAuditPage = lazy(() => import("./pages/admin/audits/I18nCoverageAuditPage"));
const PerformanceAuditPage = lazy(() => import("./pages/admin/audits/PerformanceAuditPage"));
const SeoMetadataAuditPage = lazy(() => import("./pages/admin/audits/SeoMetadataAuditPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const PromoLanding = lazy(() => import("./pages/PromoLanding"));
const PurchasesPage = lazy(() => import("./pages/admin/PurchasesPage"));
const ExpensesPage = lazy(() => import("./pages/admin/ExpensesPage"));
const UnitsSettingsPage = lazy(() => import("./pages/admin/UnitsSettingsPage"));
const SwatchInspector = lazy(() => import("./pages/admin/SwatchInspector"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const Connect = lazy(() => import("./pages/Connect"));

const queryClient = new QueryClient();

const ScrollToTop = () => {
 const { pathname } = useLocation();
 useEffect(() => {
 window.scrollTo(0, 0);
 }, [pathname]);
 return null;
};

const RouteTransition = ({ children }: { children: React.ReactNode }) => {
 const { pathname } = useLocation();
 // No fade animation inside the admin panel, it caused a flash on every navigation.
 if (pathname.startsWith("/admin")) {
 return <>{children}</>;
 }
 return (
 <div key={pathname} className="animate-fade-in">
 {children}
 </div>
 );
};

const UTMCapture = () => {
 const { pathname } = useLocation();
 useEffect(() => {
 captureUTMParams();
 }, [pathname]);
 return null;
};

// Sets robots directive + self-referencing canonical for routes that must
// not be indexed. Prevents duplicate indexing via the sitewide index.html
// canonical bleeding into transactional pages.
const NOINDEX_PATTERNS: RegExp[] = [
  /^\/cart(\/|$)/,
  /^\/checkout(\/|$)/,
  /^\/order(\/|$)/,
  /^\/login(\/|$)/,
  /^\/signup(\/|$)/,
  /^\/forgot-password(\/|$)/,
  /^\/reset-password(\/|$)/,
  /^\/account(\/|$)/,
  /^\/admin(\/|$|-login)/,
  /^\/unsubscribe(\/|$)/,
  /^\/\.lovable(\/|$)/,
  /^\/sinai(\/|$)/,
];

const RobotsDirective = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const noindex = NOINDEX_PATTERNS.some((r) => r.test(pathname));
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("robots", noindex ? "noindex, nofollow" : "index, follow");
    setMeta("googlebot", noindex ? "noindex, nofollow" : "index, follow");

    // On noindex routes, override the sitewide canonical/og:url to point at
    // the current path (never the homepage) and strip hreflang alternates.
    if (noindex) {
      const href = `https://www.nilbeautyofficial.com${pathname}`;
      let link = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
      let og = document.querySelector("meta[property='og:url']") as HTMLMetaElement | null;
      if (!og) {
        og = document.createElement("meta");
        og.setAttribute("property", "og:url");
        document.head.appendChild(og);
      }
      og.setAttribute("content", href);
      document
        .querySelectorAll("link[rel='alternate'][hreflang]")
        .forEach((n) => n.parentElement?.removeChild(n));
    }
  }, [pathname]);
  return null;
};


const DeferredCookieConsent = () => (
 <Suspense fallback={null}>
 <CookieConsent />
 </Suspense>
);

// Mount the lead-capture popup on every public marketing page (skip admin, auth, checkout flows)
const GlobalLeadCapture = () => {
 const { pathname } = useLocation();
 const blocked =
 pathname.startsWith("/admin") ||
 pathname.startsWith("/checkout") ||
 pathname.startsWith("/cart") ||
 pathname.startsWith("/order") ||
 pathname.startsWith("/login") ||
 pathname.startsWith("/signup") ||
 pathname.startsWith("/forgot-password") ||
 pathname.startsWith("/reset-password") ||
 pathname.startsWith("/account") ||
 pathname.startsWith("/unsubscribe");
 if (blocked) return null;
 return (
 <Suspense fallback={null}>
 <LeadCapturePopup />
 </Suspense>
 );
};

const App = () => (
 <QueryClientProvider client={queryClient}>
 <LanguageProvider>
 <AuthProvider>
 <ProductsProvider>
 <CartProvider>
 <DiscountBarProvider>
 <TooltipProvider>
 <Toaster />
 <Sonner />
 <BrowserRouter>
 <ScrollToTop />
 <UTMCapture />
 <RobotsDirective />
 <PageViewTracker />

 <DeferredCookieConsent />
 <GlobalLeadCapture />
 <Suspense fallback={null}>
 <RouteTransition>
 <ErrorBoundary>
 <Routes>
 <Route path="/" element={<Index />} />
 <Route path="/sinai" element={<Gate />} />
 <Route path="/about" element={<About />} />
 <Route path="/shop" element={<Shop />} />
 <Route path="/product/:id" element={<ProductPage />} />
 <Route path="/wholesale" element={<Wholesale />} />
 <Route path="/contact" element={<Contact />} />
 <Route path="/cart" element={<Cart />} />
 <Route path="/checkout" element={<Checkout />} />
 <Route path="/order/:orderNumber" element={<OrderConfirmation />} />
 <Route path="/blog" element={<Blog />} />
 <Route path="/blog/:slug" element={<BlogPostPage />} />
 <Route path="/login" element={<Login />} />
 <Route path="/signup" element={<Signup />} />
 <Route path="/forgot-password" element={<ForgotPassword />} />
 <Route path="/reset-password" element={<ResetPassword />} />
 <Route path="/account" element={<Account />} />
                 <Route path="/promo/:slug" element={<PromoLanding />} />
                 <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                 <Route path="/connect" element={<Connect />} />
 <Route path="/p/:slug" element={<PromoLanding />} />
 <Route path="/guides/:slug" element={<SeoGuide />} />
 <Route path="/ingredients" element={<IngredientsPage />} />
 <Route path="/ingredients/:slug" element={<IngredientDetailPage />} />
 <Route path="/privacy" element={<PrivacyPage />} />
 <Route path="/terms" element={<TermsPage />} />
 <Route path="/refund" element={<RefundPage />} />
 <Route path="/shipping" element={<ShippingPage />} />
 <Route path="/admin-login" element={<AdminLogin />} />
 <Route path="/admin" element={<AdminLayout />}>
 <Route index element={<DashboardPage />} />
 <Route path="dashboard" element={<DashboardPage />} />
 <Route path="swatches" element={<SwatchInspector />} />
 <Route path="dashboard/full" element={<CommandCenterPage />} />
 <Route path="insights" element={<ExecutiveSnapshotPage />} />
 <Route path="executive" element={<ExecutiveSnapshotPage />} />
 <Route path="executive/products" element={<ProductIntelligencePage />} />
 <Route path="executive/inventory" element={<InventoryIntelligencePage />} />
 <Route path="executive/trends" element={<TrendsPage />} />
 <Route path="growth" element={<GrowthPage />} />
 <Route path="landing" element={<LandingPageEditor />} />
 <Route path="site-content" element={<SiteContentPage />} />
 <Route path="navigation" element={<NavigationManagerPage />} />
 <Route path="backgrounds" element={<BackgroundsPage />} />
 <Route path="seo" element={<SeoPage />} />
 <Route path="seo-tracking" element={<SeoKeywordTrackingPage />} />
 <Route
 path="journal"
 element={
 <Suspense
 fallback={
 <div className="space-y-4">
 <Skeleton className="h-10 w-1/3" />
 <Skeleton className="h-6 w-2/3" />
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
 <Skeleton className="h-40" />
 <Skeleton className="h-40" />
 <Skeleton className="h-40" />
 </div>
 </div>
 }
 >
 <JournalPage />
 </Suspense>
 }
 />
 <Route path="lab" element={<LabLayout />}>
 <Route index element={<LabOverview />} />
 <Route path="materials" element={<LabMaterials />} />
 <Route path="materials/:id" element={<LabMaterialDetail />} />
 <Route path="suppliers" element={<LabSuppliers />} />
 <Route path="formulas" element={<LabFormulas />} />
 <Route path="production" element={<LabProduction />} />
 <Route path="inventory" element={<LabInventoryLog />} />
 </Route>
 {/* Operations domain */}
 <Route path="operations" element={<OperationsLayout />}>
 <Route index element={<ProductionActive />} />
 <Route path="active" element={<ProductionActive />} />
 <Route path="production" element={<OperationsProduction />} />
 <Route path="batches" element={<ComingSoonPage title="Batches" description="Production batch ledger with yield variance, cost snapshots, QC links and attachments." />} />
 <Route path="formulas" element={<LabFormulas />} />
 <Route path="packaging" element={<OperationsPackaging />} />
 <Route path="labor" element={<OperationsLabor />} />
 <Route path="quality-control" element={<ComingSoonPage title="Quality Control" description="Batch QC checks: pH, viscosity, color, texture, smell, pass/fail and rejection reasons." />} />
 <Route path="waste" element={<ComingSoonPage title="Waste Tracking" description="Track waste, damaged and expired stock with reason codes and value impact." />} />
 {/* legacy aliases */}
 <Route path="materials" element={<OperationsMaterials />} />
 <Route path="expenses" element={<OperationsExpenses />} />
 <Route path="cost-engine" element={<OperationsCostEngine />} />
 <Route path="profitability" element={<GrowthPage />} />
 <Route path="suppliers" element={<LabSuppliers />} />
 <Route path="inventory-log" element={<LabInventoryLog />} />
 </Route>

 {/* Inventory domain */}
 <Route path="inventory" element={<InventoryOverview />} />
 <Route path="inventory/overview" element={<InventoryOverview />} />
 <Route path="inventory/receiving" element={<ComingSoonPage title="Receiving" description="Receive PO line items into inventory with lot tracking and weighted-average costing." />} />
 <Route path="inventory/raw-materials" element={<LabMaterials />} />
 <Route path="inventory/raw-materials/:id" element={<LabMaterialDetail />} />
 <Route path="inventory/packaging" element={<OperationsPackaging />} />
 <Route path="inventory/ledger" element={<LabInventoryLog />} />
 <Route path="inventory/locations" element={<ComingSoonPage title="Warehouses & Locations" description="Per-location stock balances across warehouse, production, shipping and retail." />} />
 <Route path="inventory/adjustments" element={<ComingSoonPage title="Stock Adjustments" description="Manual inventory adjustments with required reasons and approval workflow." />} />
 <Route path="inventory/reservations" element={<InventoryReservationsPage />} />
 <Route path="inventory/finished-goods" element={<FinishedGoodsPage />} />

 {/* Purchasing domain */}
 <Route path="purchasing/suppliers" element={<LabSuppliers />} />
 <Route path="purchasing/orders" element={<ComingSoonPage title="Purchase Orders" description="Create, track and receive POs with automatic supplier price history." />} />
 <Route path="purchasing/receiving" element={<ComingSoonPage title="Receiving" description="Receive PO line items into inventory with lot tracking and weighted-average costing." />} />
 <Route path="purchasing/metrics" element={<ComingSoonPage title="Supplier Metrics" description="Lead times, on-time rates, price variance and reliability scoring per supplier." />} />

 {/* Finance domain */}
 <Route path="finance/expenses" element={<OperationsExpenses />} />
 <Route path="finance/cost-engine" element={<OperationsCostEngine />} />
 <Route path="finance/profitability" element={<GrowthPage />} />
 <Route path="finance/unit-economics" element={<ComingSoonPage title="Unit Economics" description="Per-SKU margin, labor share, packaging share and 30-day revenue ranking." />} />
 <Route path="finance/overhead" element={<ComingSoonPage title="Overhead Allocation" description="Configure allocation basis (per unit, per labor hour, per batch, fixed) for overhead lines." />} />
 <Route path="finance/campaigns" element={<FinanceCampaigns />} />
 <Route path="finance/cash" element={<FinanceCash />} />
 <Route path="finance/order-pnl" element={<FinanceOrderPnL />} />
 <Route path="finance/period-pnl" element={<FinancePeriodPnL />} />
 <Route path="finance/product-profitability" element={<ProductProfitabilityPage />} />

 {/* Analytics domain */}
 <Route path="analytics/overview" element={<OperationsOverview />} />
 <Route path="analytics/production" element={<ComingSoonPage title="Production Analytics" description="Throughput, yield variance trends, labor efficiency and batch cost stability." />} />
 <Route path="analytics/inventory" element={<ComingSoonPage title="Inventory Analytics" description="Turnover, days-on-hand, low-stock heatmap and reorder velocity." />} />
 <Route path="analytics/dead-inventory" element={<ComingSoonPage title="Dead Inventory" description="Items with no movement in the last 90 days and tied-up capital." />} />
 <Route path="analytics/margins" element={<ComingSoonPage title="Margin Trends" description="Gross and net margin evolution per product and per channel over time." />} />

 {/* Administration extras */}
 <Route path="administration/financial-periods" element={<ComingSoonPage title="Financial Periods" description="Open and close monthly periods to lock historical financial records." />} />
 <Route path="administration/settings" element={<ComingSoonPage title="System Settings" description="Global system preferences, units, rounding rules and integration toggles." />} />
 <Route path="settings/units" element={<UnitsSettingsPage />} />
 <Route path="purchases" element={<PurchasesPage />} />
 <Route path="expenses" element={<ExpensesPage />} />
 <Route path="products" element={<ProductsPage />} />
 <Route path="orders" element={<OrdersPage />} />
 <Route path="checkout-status" element={<CheckoutStatusPage />} />
 <Route path="orders/pipeline" element={<SalesPipelinePage />} />
 <Route path="customers" element={<CustomersPage />} />
 <Route path="customers/:id" element={<CustomerProfilePage />} />
 <Route path="campaigns" element={<CampaignsPage />} />
 <Route path="ad-landing-pages" element={<LandingPagesAdPage />} />
 <Route path="leads" element={<LeadsPage />} />
 <Route path="promo-codes" element={<PromoCodesPage />} />
 <Route path="favorites" element={<FavoritesPage />} />
 <Route path="shares" element={<SharesPage />} />
 <Route path="logs" element={<LogsPage />} />
 <Route path="emails" element={<EmailsPage />} />
 <Route path="users" element={<AdminUsersPage />} />
 <Route path="staff" element={<StaffManagementPage />} />
 <Route path="trusted-devices" element={<TrustedDevicesPage />} />
 <Route path="user-management" element={<UserManagementPage />} />
 <Route path="security" element={<SecurityPage />} />
 <Route path="ai-settings" element={<AiSettingsPage />} />
 <Route path="content-studio" element={<ContentStudioPage />} />
 <Route path="audits" element={<AuditsIndexPage />} />
 <Route path="audits/orders-funnel" element={<OrdersFunnelAuditPage />} />
 <Route path="audits/blog-performance" element={<BlogPerformanceAuditPage />} />
 <Route path="audits/auth-security" element={<AuthSecurityAuditPage />} />
 <Route path="audits/edge-functions" element={<EdgeFunctionsAuditPage />} />
 <Route path="audits/database-health" element={<DatabaseHealthAuditPage />} />
 <Route path="audits/metrics-endpoints" element={<MetricsEndpointsAuditPage />} />
 <Route path="audits/i18n-coverage" element={<I18nCoverageAuditPage />} />
 <Route path="audits/performance" element={<PerformanceAuditPage />} />
 <Route path="audits/seo-metadata" element={<SeoMetadataAuditPage />} />
 <Route path="*" element={<AdminNotFound />} />
 </Route>
 <Route path="/unsubscribe" element={<Unsubscribe />} />
 <Route path="*" element={<NotFound />} />
 </Routes>
 </ErrorBoundary>
 </RouteTransition>
 </Suspense>
 </BrowserRouter>
 </TooltipProvider>
 </DiscountBarProvider>
 </CartProvider>
 </ProductsProvider>
 </AuthProvider>
 </LanguageProvider>
 </QueryClientProvider>
);

export default App;
